import { differenceInSeconds, parseISO } from 'date-fns';
import humanizeDuration from 'humanize-duration';

import { useChallengeTimer } from 'play/support/useChallengeTimer';
import { Track } from 'types/graphql';

import { ChallengeTimerText } from './ChallengeTimerText';

export interface ChallengeTimerProps {
  track: Pick<Track, 'ttl' | 'started' | 'show_timer'>;
  onExpire(): void;
  showExtendModal?(): void;
  canExtend?: boolean;
  className?: string;
}

export const ChallengeTimer = ({
  track,
  onExpire,
  className,
  showExtendModal,
  canExtend = false,
}: ChallengeTimerProps) => {
  const expiresOn = track.ttl ? parseISO(track.ttl) : new Date(0);

  const { expired, timeleft } = useChallengeTimer({
    expiresOn: expiresOn.valueOf() / 1000,
    onExpire,
  });

  const timeSinceStart = differenceInSeconds(Date.now(), parseISO(track.started));
  const totalTime = differenceInSeconds(expiresOn, parseISO(track.started));

  const percentage = 100 - Math.floor((timeSinceStart / totalTime) * 100);
  const showTimer = percentage <= 10 || track.show_timer;

  if (timeleft === 30 && canExtend) {
    showExtendModal?.();
  }

  if (!showTimer) {
    return null;
  }

  const humanizer = timeleft < 60 ? shortHumanizer : longHumanizer;

  return (
    <ChallengeTimerText className={className}>{!expired ? humanizer(timeleft * 1000) : 'Expired'}</ChallengeTimerText>
  );
};

const longHumanizer = humanizeDuration.humanizer({
  language: 'shortEn',
  spacer: '',
  round: true,
  units: ['d', 'h', 'm'],
  languages: {
    shortEn: {
      d: () => 'd',
      h: () => 'h',
      m: () => 'm',
    },
  },
});

const shortHumanizer = humanizeDuration.humanizer({
  language: 'shortEn',
  spacer: '',
  round: true,
  units: ['s'],
  languages: {
    shortEn: {
      s: () => 's',
    },
  },
});
